import { clsx } from 'clsx';
import React from 'react';
import styles from './Link.module.css';

type LinkProps = React.HTMLAttributes<HTMLAnchorElement> & {
	/**
	 * Indicates link variant
	 * - Primary: grey
	 * - Secondary: dark-grey
	 * - Tertiary: red
	 * - Quaternary: yellow
	 * @default primary
	 */
	variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary';
	/**
	 * Indicate the title of the link to improve accessibility
	 * @type string
	 */
	titleLink?: string;
	/**
	 * Href of the link to do the redirection. Optional, if not passes this will be a span with link styles.
	 * @type string
	 */
	link?: string;
	/**
	 * Destination relation with the linker page.
	 * Normally "noopener noreferrer" is use for external pages, out of domain.
	 * @see https://developer.mozilla.org/es/docs/Web/HTML/Attributes/rel
	 */
	rel?: string;
	/**
	 * Target of the link, by default is '__self'.
	 */
	target?: '__self' | '__blank' | '__parent' | '__top';
	children?: React.ReactNode;
	className?: string;
	/**
	 * Apply default styling to the link component. For legacy use cases. It applies font styling verdana.
	 * @deprecated
	 * @default false
	 */
	defaultStyles?: boolean;
	i18nTrans?: boolean;
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
	(
		{
			children,
			link,
			variant = 'primary',
			rel = 'noopener noreferrer',
			defaultStyles,
			className,
			titleLink,
			i18nTrans = false,
			...props
		},
		ref
	) => {
		if (link === undefined) {
			return (
				<span
					ref={ref}
					title={titleLink}
					className={clsx(styles.link, styles[variant], defaultStyles && styles.defaultFont, className)}
					{...props}
				>
					{i18nTrans && titleLink ? titleLink : children}
				</span>
			);
		}

		return (
			<a
				ref={ref}
				href={link}
				rel={rel}
				title={titleLink}
				className={clsx(styles.link, styles[variant], defaultStyles && styles.defaultFont, className)}
				{...props}
			>
				{i18nTrans && titleLink ? titleLink : children}
			</a>
		);
	}
);
Link.displayName = 'LinkDigital';
